/**
 * Notification UI i18n — 알림 드롭다운 + 내 프로필 '내 알림' 탭 (v2.13).
 *
 * 정책 (cycle 46/49): fallback chain lang→en→ko, params 치환({n}), 전수 ko/en/ja.
 * 카테고리 라벨은 알림 type prefix(task./calendar./board./approval./chat) 매핑용.
 */
import {
  getSharedLang, getDateLocale, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // 헤더/공통
  'notif.title': { ko: '알림', en: 'Notifications', ja: '通知' },
  'notif.aria_list': { ko: '알림 목록', en: 'Notifications', ja: '通知一覧' },
  'notif.read_all': { ko: '모두 읽음', en: 'Mark all read', ja: 'すべて既読' },
  'notif.view_all': { ko: '전체 보기', en: 'View all', ja: 'すべて見る' },
  'notif.loading': { ko: '불러오는 중…', en: 'Loading…', ja: '読み込み中...' },
  'notif.error': { ko: '알림을 불러오지 못했습니다.', en: 'Failed to load notifications.', ja: '通知を読み込めませんでした。' },
  'notif.empty': { ko: '알림이 없습니다.', en: 'No notifications.', ja: '通知はありません。' },
  'notif.empty_unread': { ko: '읽지 않은 알림이 없습니다.', en: 'No unread notifications.', ja: '未読の通知はありません。' },
  'notif.unread_badge': { ko: '안읽음 {n}', en: '{n} unread', ja: '未読 {n}' },
  // ⚠ 목록 «안» 의 페이지네이션이다. 2026-09-07 에 머리에 「더보기」(전체 알림으로 가기)가
  //   생기면서 같은 낱말이 한 화면에 둘이 됐다 — 이쪽을 「더 불러오기」로 갈랐다.
  'notif.more': { ko: '더 불러오기', en: 'Load more', ja: 'さらに読み込む' },
  // 알림 팝업 머리 — 누르면 설정 > 알림설정 > 내 알림(전체 목록)으로 간다.
  'notif.panel.open_inbox': { ko: '더보기', en: 'See all', ja: 'すべて表示' },
  'notif.delete': { ko: '삭제', en: 'Delete', ja: '削除' },
  'notif.mark_read': { ko: '읽음 처리', en: 'Mark read', ja: '既読にする' },
  'notif.aria_item': { ko: '알림: {title}{unread}', en: 'Notification: {title}{unread}', ja: '通知: {title}{unread}' },
  'notif.aria_item_unread': { ko: ' (읽지 않음)', en: ' (unread)', ja: '（未読）' },

  // 필터 탭
  'notif.filter.all': { ko: '전체', en: 'All', ja: 'すべて' },
  'notif.filter.unread': { ko: '안읽음', en: 'Unread', ja: '未読' },

  // 내 프로필 탭
  'notif.tab.label': { ko: '내 알림', en: 'My notifications', ja: 'マイ通知' },
  'notif.tab.heading': { ko: '내 알림', en: 'My notifications', ja: 'マイ通知' },
  'notif.tab.subtitle': { ko: '받은 알림 전체를 확인하고 관리합니다', en: 'Review and manage all your notifications', ja: '受け取った通知を確認・管理します' },

  // 카테고리 라벨 (type prefix 매핑)
  // 알림 팝업 좌측 레일 (2026-08-18) — 상태바 벨이 '내 알림' 페이지로 보내는 대신 여기서 다 본다.
  'notif.panel.all': { ko: '전체 알림', en: 'All notifications', ja: 'すべての通知' },
  'notif.panel.unread': { ko: '안 읽은 알림', en: 'Unread', ja: '未読の通知' },
  'notif.panel.by_menu': { ko: '메뉴별 알림', en: 'By menu', ja: 'メニュー別' },
  'notif.panel.nav_aria': { ko: '알림 분류', en: 'Notification filters', ja: '通知の絞り込み' },
  'notif.panel.settings': { ko: '설정 및 관리', en: 'Settings & manage', ja: '設定と管理' },
  'notif.panel.empty_cat': { ko: '이 메뉴의 알림이 없습니다.', en: 'No notifications for this menu.', ja: 'このメニューの通知はありません。' },

  'notif.cat.task': { ko: '업무', en: 'Task', ja: 'タスク' },
  'notif.cat.calendar': { ko: '일정', en: 'Calendar', ja: '予定' },
  'notif.cat.board': { ko: '게시판', en: 'Board', ja: '掲示板' },
  'notif.cat.approval': { ko: '결재', en: 'Approval', ja: '決裁' },
  'notif.cat.reservation': { ko: '예약', en: 'Reservation', ja: '予約' },
  'notif.cat.chat': { ko: '채팅', en: 'Chat', ja: 'チャット' },
  'notif.cat.mail': { ko: '메일', en: 'Mail', ja: 'メール' },
  'notif.cat.attendance': { ko: '근태', en: 'Attendance', ja: '勤怠' },
  'notif.cat.system': { ko: '시스템', en: 'System', ja: 'システム' },

  // 상대 시간
  'notif.time.now': { ko: '방금 전', en: 'just now', ja: 'たった今' },
  'notif.time.min': { ko: '{n}분 전', en: '{n}m ago', ja: '{n}分前' },
  'notif.time.hour': { ko: '{n}시간 전', en: '{n}h ago', ja: '{n}時間前' },
  'notif.time.day': { ko: '{n}일 전', en: '{n}d ago', ja: '{n}日前' },
};

export type NotificationI18nKey = keyof typeof MESSAGES;

export function tNotif(key: NotificationI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

export function subscribeNotifLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
export function getNotifLang(): Lang { return getSharedLang(); }

/** 상대 시간 표기 — 최근은 상대, 7일 초과는 로케일 날짜. */
export function formatNotifTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const diffMs = Date.now() - d.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return tNotif('notif.time.now');
  if (min < 60) return tNotif('notif.time.min', { n: min });
  const hour = Math.floor(min / 60);
  if (hour < 24) return tNotif('notif.time.hour', { n: hour });
  const day = Math.floor(hour / 24);
  if (day < 7) return tNotif('notif.time.day', { n: day });
  return d.toLocaleDateString(getDateLocale(), { year: 'numeric', month: 'short', day: 'numeric' });
}

// 전수 가드용 export (i18n 누락 런타임 은닉 방어)
export const NOTIFICATION_I18N_MESSAGES = MESSAGES;
